import {FC, KeyboardEvent, useRef, useState} from 'react';
import {Box, CircularProgress, IconButton, TextField, Tooltip} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import StopRoundedIcon from '@mui/icons-material/StopRounded';
import {useIntl} from 'react-intl';
import {inputSx} from './ChatInput.sx';

interface ChatInputProps {
    onSend: (message: string) => void;
    disabled: boolean;
    placeholder?: string;
    elevated?: boolean;
    // While the agent is answering the send button becomes a Stop button. Omit
    // `onStop` and the composer keeps its plain send-only behaviour.
    isStreaming?: boolean;
    isStopping?: boolean;
    onStop?: () => void;
}

export const ChatInput: FC<ChatInputProps> = ({
    onSend, disabled, placeholder, elevated, isStreaming, isStopping, onStop,
}) => {
    const [value, setValue] = useState('');
    const inputRef = useRef<HTMLInputElement>(null);
    const intl = useIntl();

    const handleSend = () => {
        const trimmed = value.trim();
        if (!trimmed || disabled) return;
        onSend(trimmed);
        setValue('');
        inputRef.current?.focus();
    };

    const handleKeyDown = (e: KeyboardEvent) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSend();
        }
    };

    const showStop = Boolean(isStreaming && onStop);

    return (
        <Box sx={elevated ? inputSx.rootElevated : inputSx.root}>
            <TextField
                sx={elevated ? inputSx.textFieldElevated : inputSx.textField}
                value={value}
                onChange={(e) => setValue(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder={placeholder ?? intl.formatMessage({id: 'aiAgentPlaceholder'})}
                multiline
                maxRows={4}
                disabled={disabled}
                autoFocus
                size="small"
                inputRef={inputRef}
            />
            {showStop ? (
                <Tooltip title={intl.formatMessage({id: 'aiAgentStop'})}>
                    {/* span: a disabled IconButton fires no events, so the Tooltip needs
                        a wrapper to stay hoverable while the stop is in flight. */}
                    <span>
                        <IconButton
                            sx={inputSx.stopButton}
                            onClick={onStop}
                            disabled={isStopping}
                            aria-label={intl.formatMessage({id: 'aiAgentStop'})}
                        >
                            {/* The square reads optically smaller than the send arrow at
                                the same size, so it's nudged up to match its weight. */}
                            {isStopping
                                ? <CircularProgress size={18} color="inherit" />
                                : <StopRoundedIcon sx={{fontSize: 22}} />}
                        </IconButton>
                    </span>
                </Tooltip>
            ) : (
                <IconButton
                    sx={inputSx.sendButton}
                    onClick={handleSend}
                    disabled={disabled || !value.trim()}
                >
                    <SendIcon fontSize="small" />
                </IconButton>
            )}
        </Box>
    );
};
